import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { styled } from '@linaria/react'
import { Link } from '@tanstack/router'
import { useEffect,useState } from 'react'
import { useTranslation } from 'react-i18next'

import useCopy from '../../hooks/useCopyText'
import useMemberInfo from '../../hooks/useMemberInfo'
import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import { api } from '../../utils/api-client'
import Backdrop from '../common/backdrop'
import LogoutModal from '../common/log-out-modal'
import Icon from '../icon'
import DepositWithdraw from './deposit-withdraw'
import LanguageSelector from './language-selector'
import LoginSignUp from './login-sign-up'
import Tabs from './tabs'
import ThemeSwitch from './theme-switch'

const Drawer = styled.div`
  position: fixed;
  top: 0;
  left: -100%;
  z-index: 160;
  background-color: var(--primary);
  transition: all 0.3s ease-in-out;
  @keyframes animation {
    0% {
      left: -100%;
      opacity: 0;
    }
    100% {
      left: 0;
      opacity: 1;
    }
  }
  animation: animation 0.3s ease-in-out;
  width: 330px;
  height: 100vh;
  opacity: 0;
  pointer-events: none;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 125px;
  &.visible {
    left: 0;
    pointer-events: auto;
    opacity: 1;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 17px;
`

const WelcomeName = styled.div`
  font-size: 16px;
  color: #888888;
  font-family: 'Poppins';
  text-transform: uppercase;
`
const Badge = styled.div`
  color: #ffffff;
  background: #ff4040;
  position: absolute;
  font-size: 9px;
  width: 13px;
  height: 13px;
  padding-top: 1px;
  top: -7px;
  text-align: center;
  left: 12px;
  border-radius: 15px;
`
const Logout = styled.button`
  color: var(--highlight);
  text-transform: uppercase;
  font-size: 16px;
  padding: 10px 15px 10px 30px;
  width: 100%;
  .theme-dark & {
    border-bottom: 1px solid #1f223c;
  }
  .theme-light & {
    border-bottom: 1px solid #f5f6f9;
  }
  svg {
    fill: var(--highlight);
  }
`

const Sidebar = () => {
  const username = useBoundStore(state => state.user.username)
  const { copyTextToClipboard } = useCopy()
  const { refreshMemberInfo } = useMemberInfo()
  const modal = useModal()
  const logOutModal = useModal(LogoutModal)
  const [noOfUnread, setNoOfUnread] = useState(0)
  const { t } = useTranslation(['common', 'drawer'])

  useEffect(() => {
    if (!username || !modal.visible) return
    refreshMemberInfo()
  }, [modal.visible])

  useEffect(() => {
    if (!username || !modal.visible) return

    const fetchNumberOfUnreadMessages = async () => {
      const response = await api.post<ApiResponse<string>>('/ajax_data.php', {
        type: 'noread_message',
      })
      const { status, info } = response.data
      if (status === 1) {
        setNoOfUnread(parseInt(info))
      }
    }
    fetchNumberOfUnreadMessages()
  }, [username, modal.visible])

  return (
    <>
      <Backdrop
        zIndex={150}
        isVisible={modal.visible}
        onClick={() => modal.hide()}
        id="profile-drawer"
      />
      <Drawer className={modal.visible ? 'visible' : ''}>
        <Header>
          <div>
            <WelcomeName
              className='flx-str-ctr'
              onClick={() => copyTextToClipboard(username, 'Copiado')}  
            >
              {t('drawer:welcome-guest')}
              {username ? ',' : '!'}
              {` ${username}${username && '!'}`}
              {username && (
                <Icon
                  id="copy"
                  width={24}
                  height={24}
                  style={{ marginLeft: 2 }}
                />
              )}
            </WelcomeName>
          </div>
          <Link
            to="/profile/inbox"
            style={{ position: 'relative' }}
            onClick={() => modal.hide()}>
            {username && noOfUnread > 0 ? <Badge>{noOfUnread}</Badge> : null}
            <Icon id="envelope" width="20" height="16" />
          </Link>
        </Header>
        {!username ? (
          <LoginSignUp closeProfileDrawer={() => modal.hide()} />
        ) : (
          <DepositWithdraw closeProfileDrawer={() => modal.hide()} />
        )}
        <Tabs
          closeProfileDrawer={() => modal.hide()}
          showProfileDrawer={() => modal.show()}
        />
        {username && (
          <Logout
            className="flx-btw-ctr"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              modal.hide()
              logOutModal.show()
            }}>
            {t('logout', { ns: 'drawer' })}
          </Logout>
        )}
        <ThemeSwitch />
        <LanguageSelector />
      </Drawer>
    </>
  )
}

export default NiceModal.create(Sidebar)
